/**
 * Панель-каталог справа (фидбек владельца 27.07): все объекты площадки,
 * сгруппированные по категориям (Магазины/Склады/Офисы/…/Парковки/Здания),
 * живой фильтр сверху, наведение подсвечивает объект на карте, клик
 * открывает карточку.
 */
import { tr } from '@app/i18n'
import { useState } from 'preact/hooks'
import { selectTarget } from '../actions'
import { markerVisual } from '../map/marker-icons'
import { buildSearchIndex, type SearchEntry } from '../search'
import { queryVariants, scoreKeyword } from '../search-text'
import { catalogOpen, langTick, liveOccupants, venue, venueMap } from '../state'
import { dedupeEntries } from './dedupe'
import { categoryLabel, featureName } from './labels'
import { useDragPanel } from './use-drag'

/** Порядок секций: сначала фирмы по типам, затем сервисы, затем здания. */
const SECTION_ORDER = [
  'shop',
  'warehouse',
  'office',
  'cafe',
  'auto',
  'factory',
  'parking',
  'checkpoint',
  'info',
  'wc',
  '__buildings',
  '__other',
] as const

function sectionOf(e: SearchEntry): string {
  if (e.kind === 'building') return '__buildings'
  return (SECTION_ORDER as readonly string[]).includes(e.category) ? e.category : '__other'
}

function sectionTitle(key: string): string {
  if (key === '__buildings') return tr('catalog.buildings')
  if (key === '__other') return tr('catalog.other')
  return categoryLabel(key)
}

/** Живой фильтр: умный скоринг (опечатки/транслит) по имени и категории. */
function matchesFilter(vName: string, catName: string, variants: readonly string[]): boolean {
  if (variants.length === 0) return true
  const hay = [vName.toLowerCase(), catName.toLowerCase()]
  return variants.some((needle) => hay.some((h) => scoreKeyword(needle, h) >= 34))
}

function Row({ entry }: { entry: SearchEntry }) {
  const v = venue.value
  if (!v) return null
  const { svg, color } = markerVisual(entry.category)
  return (
    <button
      type="button"
      class="cat-row"
      onMouseEnter={() => venueMap.value?.highlightMany([entry.id])}
      onMouseLeave={() => venueMap.value?.highlightMany([])}
      onClick={() => {
        catalogOpen.value = false
        venueMap.value?.highlightMany([])
        selectTarget(entry.id)
      }}
    >
      <span
        class="cat-row-icon"
        style={{ '--mk': color }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <span class="cat-row-text">
        <span class="cat-row-name">{featureName(v, entry.id)}</span>
        <span class="cat-row-sub">{categoryLabel(entry.category)}</span>
      </span>
    </button>
  )
}

export function CatalogPanel() {
  langTick.value
  const drag = useDragPanel('catalog')
  const [filter, setFilter] = useState('')
  const v = venue.value
  if (!v || !catalogOpen.value) return null
  const variants = queryVariants(filter)
  const index = dedupeEntries(v, buildSearchIndex(v, liveOccupants.value)).filter((e) =>
    matchesFilter(featureName(v, e.id), categoryLabel(e.category), variants),
  )
  const groups = new Map<string, SearchEntry[]>()
  for (const e of index) {
    const key = sectionOf(e)
    const list = groups.get(key) ?? []
    list.push(e)
    groups.set(key, list)
  }
  const byName = (a: SearchEntry, b: SearchEntry): number =>
    featureName(v, a.id).localeCompare(featureName(v, b.id), 'ru')
  return (
    <div
      ref={drag.elRef as never}
      style={drag.style}
      class="cat-panel glass"
      role="dialog"
      aria-label={tr('catalog.title')}
    >
      <div
        class="cat-head drag-handle"
        onPointerDown={(e) => drag.onDragStart(e as unknown as PointerEvent)}
      >
        <span class="tech-label">{tr('catalog.title')}</span>
        <button
          type="button"
          class="icon-btn"
          aria-label={tr('common.close')}
          onClick={() => {
            catalogOpen.value = false
            venueMap.value?.highlightMany([])
          }}
        >
          ✕
        </button>
      </div>
      <div class="cat-filter">
        <input
          type="search"
          class="cat-filter-input"
          placeholder={tr('catalog.filter_placeholder')}
          value={filter}
          onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
        />
      </div>
      <div class="cat-body">
        {SECTION_ORDER.map((key) => {
          const list = groups.get(key)
          if (!list || list.length === 0) return null
          return (
            <div key={key}>
              <p class="search-section-title">
                {sectionTitle(key)} · {list.length}
              </p>
              {list.sort(byName).map((e) => (
                <Row key={e.id} entry={e} />
              ))}
            </div>
          )
        })}
        {index.length === 0 && <p class="cat-empty">{tr('search.not_found', { q: filter })}</p>}
      </div>
      <p class="cat-foot">{tr('catalog.hover_hint')}</p>
    </div>
  )
}
